import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { messages } from './use-create-mapping-messages';

/**
 * @typedef {import("../../data/types/types").MappedColumn} MappedColumn
 * @typedef {import("../../data/types/types").MappingError} MappingError
 * @typedef {import("../../data/types/types").MappingErrors} MappingErrors
 * @typedef {import("../../data/types/types").TemplateAttribute} TemplateAttribute
 */

/**
 * @typedef {object} Validator
 * @property {(mappedColumn?: MappedColumn) => boolean} validate
 * @property {(mappedColumn?: MappedColumn) => React.JSX.Element} getMessage
 */

/** @type {Record<string, Validator>} */
const validators = {
  isRequired: {
    validate: (mappedColumn) => !!mappedColumn?.csvColumn,
    getMessage: () => <FormattedMessage {...messages.requiredValidationMessage} />,
  },
};

/**
 * @param {MappedColumn} csvColumn
 * @param {string} templateAttributeCode
 * @returns {boolean}
 */
const isColumnMatching = (csvColumn, templateAttributeCode) => {
  return csvColumn.code === templateAttributeCode;
};

/**
 * @param {string} attributeCode
 * @param {string | null} csvColumn
 * @param {boolean} isSuggested
 * @returns {MappedColumn}
 */
const createMappedColumn = (attributeCode, csvColumn = null, isSuggested) => {
  return {
    code: attributeCode,
    csvColumn,
    isSuggested,
  };
};

/**
 * @param {TemplateAttribute[]} attributes
 * @returns {MappedColumn[]}
 */
const createMappingColumns = (attributes) => {
  return attributes.map((attribute) => createMappedColumn(attribute.code, attribute.csvColumnName, attribute.isSuggested));
};

/**
 * @param {MappedColumn[]} mappings
 * @param {string[]} templateColumns
 * @returns {string[]}
 */
const getRemainingCsvColumns = (mappings, templateColumns) => {
  return Object.keys(templateColumns).reduce((acc, csvColumn) => {
    if (!mappings.some((mappedColumn) => mappedColumn.csvColumn === templateColumns[csvColumn])) {
      return [...acc, templateColumns[csvColumn]];
    }
    return acc;
  }, []);
};

/**
 * @param {MappedColumn | undefined} mappedColumn
 * @param {TemplateAttribute} templateAttribute
 * @returns {MappingError[]}
 */
const validateSingleMapping = (mappedColumn, templateAttribute) => {
  return Object.keys(validators).reduce((errors, key) => {
    const attributeValidator = templateAttribute[key];
    if (!attributeValidator) {
      return errors;
    }
    const validator = validators[key];
    const isValid = validator.validate(mappedColumn);
    if (isValid) {
      return errors;
    }
    return [
      ...errors,
      {
        attributeCode: templateAttribute.code,
        message: validator.getMessage(mappedColumn),
      },
    ];
  }, []);
};

/**
 *
 * @param {Array<import("../../data/types/types").TemplateAttribute>} templateAttributes
 * @param {MappedColumn[]} mappedColumns
 * @returns {MappingErrors}
 */
const validateMapping = (templateAttributes, mappedColumns) => {
  const errors = /** @type {MappingErrors} */ ({});

  templateAttributes.forEach((attribute) => {
    const mappedColumn = mappedColumns.find((column) => isColumnMatching(column, attribute.code));

    const columnErrors = validateSingleMapping(mappedColumn, attribute);

    errors[attribute.code] = columnErrors;
  });

  return errors;
};

/**
 * @param {import("../../data/types/types").InitImportDefinition} template
 * @returns {import("../../data/types/types").CreateMappingHookReturn}
 */
export const useCreateMapping = (template) => {
  const [mapping, setMapping] = useState(
    /** @type {{ mappedColumns: MappedColumn[]; mappingErrors: MappingErrors; remainingColumns: string[] }} */ ({
      mappedColumns: [],
      mappingErrors: {},
      remainingColumns: [],
    }),
  );

  /**
   * @param {MappingErrors} mappingErrors
   * @returns {boolean}
   */
  const isMappingResultValid = (mappingErrors) => !Object.values(mappingErrors).some((value) => value?.length > 0);

  useEffect(() => {
    const mappedColumns = createMappingColumns(template.attributes);

    setMapping({
      mappedColumns,
      mappingErrors: validateMapping(template.attributes, mappedColumns),
      remainingColumns: getRemainingCsvColumns(mappedColumns, template.columns),
    });
  }, [template.attributes, template.columns]);

  /**
   * @returns {boolean}
   */
  const validateUserMapping = () => {
    const mappingErrors = validateMapping(template.attributes, mapping.mappedColumns);
    setMapping((prev) => ({ ...prev, mappingErrors }));

    return isMappingResultValid(mappingErrors);
  };

  /**
   * @param {string} attributeCode
   * @returns {MappingError[]}
   */
  const getMappingErrors = (attributeCode) => {
    return mapping.mappingErrors[attributeCode] ?? [];
  };

  /**
   * @param {TemplateAttribute} templateAttribute
   * @param {string} csvColumn
   * @returns {void}
   */
  const updateMapping = (templateAttribute, csvColumn) => {
    const errors = /** @type {MappingErrors} */ ({});
    const mappedColumns = /** @type {MappedColumn[]} */ ([]);

    mapping.mappedColumns.forEach((mappedColumn) => {
      if (isColumnMatching(mappedColumn, templateAttribute.code)) {
        const updatedMapping = createMappedColumn(mappedColumn.code, csvColumn, false);

        const columnErrors = validateSingleMapping(updatedMapping, templateAttribute);
        errors[templateAttribute.code] = columnErrors;

        mappedColumns.push(updatedMapping);
      } else {
        mappedColumns.push(mappedColumn);
      }
    });

    const mappingErrors = { ...mapping.mappingErrors, ...errors };

    setMapping({
      mappedColumns,
      mappingErrors,
      remainingColumns: getRemainingCsvColumns(mappedColumns, template.columns),
    });
  };

  const getMappedCsvColumn = useCallback(
    /**
     * @param {TemplateAttribute} templateAttribute
     * @returns {MappedColumn | undefined}
     */
    (templateAttribute) => {
      return mapping.mappedColumns.find((mappedColumn) => isColumnMatching(mappedColumn, templateAttribute.code));
    },
    [mapping.mappedColumns],
  );

  return {
    mappedColumns: mapping.mappedColumns,
    remainingColumns: mapping.remainingColumns,
    isMappingResultValid: isMappingResultValid(mapping.mappingErrors),

    updateMapping,
    getMappedCsvColumn,

    getMappingErrors,
    validateUserMapping,
  };
};
